import React, { useState } from "react";
import { IDENTITY_CDL, IDENTITY_ADJUST } from "./gradeMath";
import { CurveEditor } from "./CurveEditor";
import { QualifierControls } from "./QualifierControls";

const Slider = ({ label, value, min, max, step, onChange, testid, accent }) => (
  <div className="flex items-center gap-2 text-xs">
    <span className={`w-14 font-mono uppercase tracking-wide ${accent || "text-white/40"}`}>{label}</span>
    <input type="range" min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="flex-1 accent-orange-500" data-testid={testid} />
    <span className="w-12 text-right font-mono text-white/60">{Number(value).toFixed(2)}</span>
  </div>
);

const WheelGroup = ({ title, rgb, master, onRgb, onMaster, range, testPrefix }) => (
  <div className="space-y-1.5 p-3 rounded-lg bg-white/[0.03] border border-white/10">
    <h4 className="text-[10px] font-mono uppercase tracking-widest text-orange-300/80">{title}</h4>
    {["R", "G", "B"].map((ch, c) => (
      <Slider key={ch} label={ch} value={rgb?.[c] || 0} min={-range} max={range} step={0.005}
        accent={["text-red-400/70", "text-green-400/70", "text-blue-400/70"][c]}
        onChange={(v) => { const next = [...(rgb || [0, 0, 0])]; next[c] = v; onRgb(next); }}
        testid={`${testPrefix}-${ch.toLowerCase()}`} />
    ))}
    <Slider label="MASTER" value={master || 0} min={-range} max={range} step={0.005}
      onChange={onMaster} testid={`${testPrefix}-master`} />
  </div>
);

const Section = ({ title, children, defaultOpen = false, testid }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border border-white/10 overflow-hidden">
      <button onClick={() => setOpen(!open)} data-testid={testid}
        className="w-full flex items-center justify-between px-3 py-2 bg-white/[0.04] text-left">
        <span className="text-[10px] font-mono uppercase tracking-widest text-white/60">{title}</span>
        <span className="text-white/30 text-xs">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="p-2 space-y-2">{children}</div>}
    </div>
  );
};

/** Controls for the ACTIVE node — Primaries (CDL), Adjustments, Curves,
 *  HSL Qualifier, LUT node (spec §2.2 items 1, 2, 3, 4, 8). */
export const NodeControls = ({ node, onChange, matteOn, onMatteToggle, onLutUpload }) => {
  const [uploading, setUploading] = useState(false);
  if (!node) return <p className="text-xs text-white/30 italic">Select a node.</p>;
  const cdl = { ...IDENTITY_CDL(), ...(node.ops?.cdl || {}) };
  const adj = { ...IDENTITY_ADJUST(), ...(node.ops?.adjust || {}) };

  const setOps = (patch) => onChange({ ...node, ops: { ...node.ops, ...patch } });
  const setCdl = (patch) => setOps({ cdl: { ...cdl, ...patch } });
  const setAdj = (patch) => setOps({ adjust: { ...adj, ...patch } });

  const onLut = async (file) => {
    if (!file || !onLutUpload) return;
    setUploading(true);
    try {
      const { url, name } = await onLutUpload(file);
      setOps({ lut_ref: { name, url, intensity: 1.0 } });
    } finally { setUploading(false); }
  };

  return (
    <div className="space-y-2" data-testid="grade-node-controls">
      <h3 className="text-xs font-mono uppercase tracking-widest text-white/40">
        {node.label}
      </h3>

      <Section title="Primaries" defaultOpen testid="section-primaries">
        <WheelGroup title="Lift" rgb={cdl.lift} master={cdl.lift_master} range={0.5}
          onRgb={(v) => setCdl({ lift: v })} onMaster={(v) => setCdl({ lift_master: v })}
          testPrefix="cdl-lift" />
        <WheelGroup title="Gamma" rgb={cdl.gamma} master={cdl.gamma_master} range={0.7}
          onRgb={(v) => setCdl({ gamma: v })} onMaster={(v) => setCdl({ gamma_master: v })}
          testPrefix="cdl-gamma" />
        <WheelGroup title="Gain" rgb={cdl.gain} master={cdl.gain_master} range={0.7}
          onRgb={(v) => setCdl({ gain: v })} onMaster={(v) => setCdl({ gain_master: v })}
          testPrefix="cdl-gain" />
        <WheelGroup title="Offset" rgb={cdl.offset} master={cdl.offset_master} range={0.3}
          onRgb={(v) => setCdl({ offset: v })} onMaster={(v) => setCdl({ offset_master: v })}
          testPrefix="cdl-offset" />
      </Section>

      <Section title="Adjustments" testid="section-adjustments">
        <Slider label="CONTR" value={adj.contrast} min={0.2} max={2.5} step={0.01}
          onChange={(v) => setAdj({ contrast: v })} testid="adj-contrast" />
        <Slider label="PIVOT" value={adj.pivot} min={0} max={1} step={0.005}
          onChange={(v) => setAdj({ pivot: v })} testid="adj-pivot" />
        <Slider label="SAT" value={adj.saturation} min={0} max={2.5} step={0.01}
          onChange={(v) => setAdj({ saturation: v })} testid="adj-saturation" />
        <Slider label="TEMP" value={adj.temp} min={-1} max={1} step={0.005}
          onChange={(v) => setAdj({ temp: v })} testid="adj-temp" />
        <Slider label="TINT" value={adj.tint} min={-1} max={1} step={0.005}
          onChange={(v) => setAdj({ tint: v })} testid="adj-tint" />
      </Section>

      <Section title="Curves" testid="section-curves">
        <CurveEditor curves={node.ops?.curves || {}}
          onChange={(curves) => setOps({ curves })} />
      </Section>

      <Section title="Qualifier" testid="section-qualifier">
        <QualifierControls qualifier={node.ops?.qualifier}
          onChange={(qualifier) => setOps({ qualifier })}
          matteOn={matteOn} onMatteToggle={onMatteToggle} />
      </Section>

      <Section title="LUT" testid="section-lut">
        {node.ops?.lut_ref ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-white/70 font-mono truncate">{node.ops.lut_ref.name}</span>
              <button onClick={() => setOps({ lut_ref: null })}
                className="text-red-400/60 hover:text-red-400" data-testid="lut-remove-btn">✕</button>
            </div>
            <Slider label="MIX" value={node.ops.lut_ref.intensity ?? 1} min={0} max={1} step={0.01}
              onChange={(v) => setOps({ lut_ref: { ...node.ops.lut_ref, intensity: v } })}
              testid="lut-intensity" />
            <p className="text-[9px] text-white/25">Preview switches to CPU engine while a LUT is active.</p>
          </div>
        ) : (
          <label className="block text-xs text-white/40 cursor-pointer hover:text-orange-300 transition-colors">
            {uploading ? "Uploading…" : "Import .cube LUT"}
            <input type="file" accept=".cube" className="hidden" data-testid="lut-file-input"
              disabled={uploading}
              onChange={(e) => onLut(e.target.files?.[0])} />
          </label>
        )}
      </Section>
    </div>
  );
};
